import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type ColumnDef } from '@tanstack/react-table';
import { BookOpen } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { getCenterName, getPatientName, getStatusBadge } from 'utils/appointments';
import { formatUnifiedForDisplay } from 'utils/date';
import { type NextSession } from './types';
import { useFetchNextSessions } from './useFetchNextSessions';

export const NextSessions = (): React.ReactElement => {
  const { data: appointments, isLoading, isFetching } = useFetchNextSessions();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const handleOnManageAppointment = useCallback(
    (appointment: NextSession): void => {
      navigate(
        generatePath(ROUTES.PATIENT_APPOINTMENTS, {
          id: appointment.patient.id,
          appointmentId: appointment.id,
        }),
        { state: { appointment, from: pathname } }
      );
    },
    [navigate, pathname]
  );
  const nextAppointments = appointments?.filter(appointment => !appointment.canceled);

  const columns: ColumnDef<NextSession>[] = useMemo(
    () => [
      {
        accessorKey: 'dateTime',
        header: t('AppointmentsTable.date.label'),
        accessorFn: row => formatUnifiedForDisplay(row.dateTime),
        cell: ({ row }) => formatUnifiedForDisplay(row.original.dateTime),
      },
      {
        id: 'patient',
        header: t('AppointmentsTable.patient.label'),
        accessorFn: row => getPatientName(row.patient),
        cell: ({ row }) => getPatientName(row.original.patient),
      },
      {
        accessorKey: 'bookedContactMethod',
        header: t('AppointmentsTable.type.label'),
        accessorFn: row =>
          row.bookedContactMethod ? t(`AppointmentCard.types.${row.bookedContactMethod}`) : '',
        cell: ({ row }) =>
          row.original.bookedContactMethod
            ? t(`AppointmentCard.types.${row.original.bookedContactMethod}`)
            : '',
      },
      {
        id: 'center',
        header: t('AppointmentsTable.center.label'),
        accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
        cell: ({ row }): string =>
          getCenterName(row.original.bookedContactMethod, row.original.center),
      },
      {
        id: 'status',
        header: t('AppointmentsTable.status.label'),
        cell: ({ row }) => getStatusBadge(row.original, 'w-28'),
      },
      {
        id: 'actions',
        header: t('AppointmentsTable.actions.label'),
        cell: ({ row }) => (
          <Button
            variant="terciary"
            className="text-xs w-fit"
            onClick={() => handleOnManageAppointment(row.original)}
            icon="leading"
          >
            <BookOpen size={8} className="max-am-sm:hidden" />
            {t('PsychologistPatients.manageAppointment.cta', {
              ns: LOCIZE_NS.PSYCHOLOGIST,
            })}
          </Button>
        ),
      },
    ],
    [t, handleOnManageAppointment]
  );

  return (
    <ClientPaginatedTable
      data={nextAppointments ?? []}
      columns={columns}
      initialSorting={[{ id: 'dateTime', desc: true }]}
      isLoading={isLoading || isFetching}
      emptyTitle={t('PsychologistHome.empty', { ns: LOCIZE_NS.HOME })}
      title={t('PsychologistHome.heading', { ns: LOCIZE_NS.HOME })}
      withSearch
    />
  );
};
